// Logique de la simulation de salaire du formulaire de contrat
// (Yros/Support#42). Sans React, pour rester testable.

export const SIMULATION_MODES = { FROM_NET: 'net', FROM_SURSALAIRE: 'sursalaire' };

// Variables génériques du contrat de simulation côté backend.
export const GENERIC_FIELDS = ['prime_imposable', 'prime_non_imposable', 'autre_deduction'];

export const TRANSPORT_KEY = 'transport';

const toAmount = (value) => {
    const amount = parseFloat(value);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

export const IPM_KEY = 'ipm';

// La prime de transport et la cotisation IPM salariale saisies dans le
// formulaire de contrat deviennent des lignes cochables, comme les primes du
// salarié. La part patronale de l'IPM n'agit pas sur le net : elle est omise.
export const withContractElements = (selectable, contract, labels) => {
    const fromContract = [
        { key: TRANSPORT_KEY, name: labels.transport, field: 'transport_premium', amount: toAmount(contract.transport_premium) },
        { key: IPM_KEY, name: labels.ipm, field: 'ipm_contribution', amount: toAmount(contract.ipm_contribution) },
    ].filter((element) => element.amount > 0).map((element) => ({ ...element, estimation: false }));
    return [...fromContract, ...selectable];
};

// Somme, par variable générique, des primes cochées et des montants saisis
// à la main. Les montants sont toujours envoyés, même nuls : le backend ne
// déduit plus rien du salarié.
export const sumPayrollVariables = (selectable, excludedKeys, manual = {}) => {
    const totals = { transport_premium: 0, ipm_contribution: 0 };
    GENERIC_FIELDS.forEach((field) => {
        totals[field] = toAmount(manual[field]);
    });
    selectable.forEach((element) => {
        if (excludedKeys.has(element.key) || !(element.field in totals)) return;
        totals[element.field] += toAmount(element.amount);
    });
    return totals;
};

export const isSimulationReady = ({ contract, mode, netAmount }) => {
    if (!contract.convention || !contract.category || !contract.baseSalary) return false;
    return mode === SIMULATION_MODES.FROM_NET ? toAmount(netAmount) > 0 : true;
};

export const buildSimulationPayload = ({
    contract, employee, mode, netAmount, sursalaire, selectable, excludedKeys, manual,
}) => {
    const fromNet = mode === SIMULATION_MODES.FROM_NET;
    return {
        convention: contract.convention,
        category: contract.category,
        baseSalary: contract.baseSalary,
        executive: !!contract.is_executive,
        maritalStatus: employee?.maritalStatus || 'single',
        spouseIncome: employee?.spouseIncome || 0,
        numberOfChildren: employee?.numberOfChildren || 0,
        foreign: false,
        // Le backend cherche le sursalaire quand additionalSalary vaut 0 et
        // qu'un net est demandé ; sinon il calcule le net du sursalaire donné.
        additionalSalary: fromNet ? 0 : toAmount(sursalaire),
        netAmount: fromNet ? toAmount(netAmount) : 0,
        ...sumPayrollVariables(selectable, excludedKeys, manual),
    };
};

// Les routes Odoo type='json' renvoient {result: {...}} ; une erreur métier
// arrive dans result.error avec un statut HTTP 200.
export const readSimulationResponse = (data) => {
    const result = data?.result;
    if (result?.success) return { result };
    return { error: result?.error || data?.error?.data?.message || null };
};

// Lignes du détail, du salaire de base au net. Le backend calcule
// net = brut + non imposable - (déductions + charges salariales), charges
// qui incluent l'impôt. Ce que le moteur de paie ajoute de lui-même (primes
// de société) apparaît dans les lignes « autres éléments », pour que le
// détail s'additionne toujours.
export const buildResultRows = (result) => {
    const amount = (value) => Number(value) || 0;
    const base = amount(result.base_salary);
    const sursalaire = amount(result.additional_salary);
    const primeImposable = amount(result.prime_imposable);
    const gross = amount(result.gross_salary);
    const charges = amount(result.total_contribution);
    const tax = amount(result.tax_amount);
    const transport = amount(result.transport_premium);
    const primeNonImposable = amount(result.prime_non_imposable);
    const deduction = amount(result.autre_deduction);
    const net = amount(result.net_salary);
    const otherGross = gross - base - sursalaire - primeImposable;
    const otherNet = net - (gross - charges + transport + primeNonImposable - deduction);

    return [
        { key: 'baseSalary', value: base, always: true },
        { key: 'sursalaire', value: sursalaire },
        { key: 'primeImposable', value: primeImposable },
        { key: 'otherGross', value: otherGross },
        { key: 'gross', value: gross, always: true, strong: true },
        { key: 'charges', value: -charges },
        { key: 'taxIncluded', value: tax, detail: true },
        { key: 'ipmIncluded', value: amount(result.ipm_contribution), detail: true },
        { key: 'transportPremium', value: transport },
        { key: 'primeNonImposable', value: primeNonImposable },
        { key: 'deduction', value: -deduction },
        { key: 'otherNet', value: otherNet },
    ].filter((row) => row.always || Math.round(row.value) !== 0);
};
